import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { AlertCircle, Infinity as InfinityIcon, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dashboard usage widget — visual meters for each plan limit (call analyses,
 * transcript minutes, voice minutes, realtime voice) plus a contextual
 * "Upgrade" CTA when any meter is ≥ 80%. Auto-hides when the /usage endpoint
 * returns nothing (e.g. free plan without usage yet). Safe: fetches once per
 * dashboard render, no polling.
 */
export default function UsageWidget() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/subscription/usage`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading || !data?.meters?.length) return null;

  const planLabel = (data.plan || 'free').charAt(0).toUpperCase() + (data.plan || 'free').slice(1);
  const anyWarn = data.meters.some(m => m.percentage >= 80 && m.limit !== -1);

  return (
    <Card className="bg-card border-border/50" data-testid="usage-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Plan usage · {planLabel}
          </CardTitle>
          {anyWarn && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-500/10 border border-amber-500/30 px-2 py-0.5 rounded-full">
              <AlertCircle className="w-3 h-3" />
              Approaching limit
            </span>
          )}
        </div>
        <button
          onClick={() => navigate('/pricing')}
          className="text-xs font-medium text-primary hover:underline flex items-center gap-1"
          data-testid="usage-upgrade-btn"
        >
          <TrendingUp className="w-3.5 h-3.5" />
          {anyWarn ? 'Upgrade plan' : 'Manage plan'}
        </button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {data.meters.map((m) => <Meter key={m.key} meter={m} />)}
        </div>
      </CardContent>
    </Card>
  );
}

function Meter({ meter }) {
  const unlimited = meter.limit === -1;
  const disabled = meter.limit === 0;
  const pct = unlimited ? 0 : meter.percentage;
  const barColor =
    pct >= 95 ? 'bg-destructive' :
    pct >= 80 ? 'bg-amber-500' :
    pct >= 50 ? 'bg-primary' :
    'bg-success';

  return (
    <div data-testid={`usage-meter-${meter.key}`}>
      <div className="flex justify-between items-baseline text-xs mb-1">
        <span className="text-muted-foreground">{meter.label}</span>
        {unlimited ? (
          <span className="flex items-center gap-0.5 text-success font-semibold">
            <InfinityIcon className="w-3 h-3" />
            <span>Unlimited</span>
          </span>
        ) : disabled ? (
          <span className="text-muted-foreground/60 text-[11px]">Not included</span>
        ) : (
          <span className="font-semibold text-foreground">
            {meter.used.toLocaleString()}
            <span className="text-muted-foreground font-normal"> / {meter.limit.toLocaleString()}</span>
          </span>
        )}
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary">
        {!unlimited && !disabled && (
          <div
            className={`h-full transition-all duration-500 ${barColor}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        )}
        {unlimited && <div className="h-full w-full bg-success/30" />}
      </div>
    </div>
  );
}
